/**
 * Artifact detail slice — data for the /artifact page.
 *
 * - fetchArtifactDetail: POST /artifacts/detail via beaconApi, mapped to the
 *   ArtifactDetailView view model. When several artifacts match, the one whose
 *   id equals the searched id is shown, else the latest (last by createdAt).
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ArtifactDetailView } from './artifactDetailTypes';
import { mapArtifactDetail } from './artifactDetailMapper';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

export interface ArtifactDetailState {
  data: ArtifactDetailView | null;
  /** Other artifacts returned for the same identifier. */
  otherArtifactIds: string[];
  loading: boolean;
  error: string | null;
  notFound: boolean;
  currentRequestId: string | null;
}

export const artifactDetailInitialState: ArtifactDetailState = {
  data: null,
  otherArtifactIds: [],
  loading: false,
  error: null,
  notFound: false,
  currentRequestId: null,
};

interface FetchResult {
  detail: ArtifactDetailView | null;
  otherArtifactIds: string[];
}

export const fetchArtifactDetail = createAsyncThunk<
  FetchResult, string, { state: RootState; rejectValue: string }
>('artifactDetail/fetch', async (artifactId, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const response = await beaconApi.getArtifactDetail([artifactId], apiBase);
    const found = response.artifacts ?? [];
    if (found.length === 0) return { detail: null, otherArtifactIds: [] };
    const primary = found.find(artifact => artifact.id === artifactId) ?? found[found.length - 1];
    return {
      detail: mapArtifactDetail(primary),
      otherArtifactIds: found.filter(artifact => artifact !== primary).map(artifact => artifact.id),
    };
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch artifact detail');
  }
});

const artifactDetailSlice = createSlice({
  name: 'artifactDetail',
  initialState: artifactDetailInitialState,
  reducers: {
    clearArtifactDetail: () => artifactDetailInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArtifactDetail.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.notFound = false;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchArtifactDetail.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.data = action.payload.detail;
        state.otherArtifactIds = action.payload.otherArtifactIds;
        state.notFound = action.payload.detail === null;
      })
      .addCase(fetchArtifactDetail.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch artifact detail';
      });
  },
});

export const { clearArtifactDetail } = artifactDetailSlice.actions;
export default artifactDetailSlice.reducer;
